import { logError } from '@core/logging';
import { AuthContext, Language, languages } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import {
    Button,
    ContainerDefault,
    ErrorMessage,
    ErrorMessagePage,
    getTranslations,
    Heading,
    Info,
    Input,
    loadPatch,
    switchLanguage,
    Upload,
    useFileDropzone
} from '@core/ui';
import { defaultResources, resources } from '@core/ui/src/locales/resources';
import { useMutation } from '@tanstack/react-query';
import i18n from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { serializeError } from 'serialize-error';

export const ManageTranslationPage = () => {
    const [language, setLanguage] = useState<Language>(i18n.language as Language);
    const [original, setOriginal] = useState<{ [key: string]: string }>({});
    const [patch, setPatch] = useState<{ [key: string]: string }>({});

    const { t } = useTranslation();

    const canManageTranslations = useContext(AuthContext).canManageTranslations;
    if (!canManageTranslations) {
        return <ErrorMessagePage statusCode={404} />;
    }

    useEffect(() => {
        const lng = languages.find((l) => l.code === language);
        if (!lng) {
            return;
        }

        switchLanguage(language).then(() => {
            const { original: o, patch: p } = getTranslations(language);

            // Remove all keys that are not present in the original translation
            Object.keys(p).forEach((key) => {
                if (!(key in o)) {
                    delete p[key];
                }
            });

            setOriginal(o);
            setPatch(p);
        });
    }, [language]);

    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }

            if (acceptedFiles[0].name.toLocaleLowerCase().endsWith('.json')) {
                acceptedFiles[0].arrayBuffer().then((data) => {
                    const decoder = new TextDecoder('utf-8');
                    const json = JSON.parse(decoder.decode(data));
                    // Only update the keys that are present in the original translation
                    const updated = { ...patch };
                    for (const key in json) {
                        if (key in original) {
                            updated[key] = json[key];
                        }
                    }
                    setPatch(updated);
                });
                return;
            }

            import('xlsx').then((xlsx) => {
                acceptedFiles[0].arrayBuffer().then((data) => {
                    try {
                        const workBook = xlsx.read(data, { type: 'array', dense: true, WTF: true });

                        const updated = { ...patch };
                        for (const sheetName of workBook.SheetNames) {
                            const sheet = workBook.Sheets[sheetName];
                            if (!sheet['!ref']) {
                                continue;
                            }
                            const range = xlsx.utils.decode_range(sheet['!ref']);

                            const sheetData: string[][] = [];
                            for (let row = range.s.r; row <= range.e.r; row++) {
                                const rowData: string[] = [];
                                for (let col = range.s.c; col <= range.e.c; col++) {
                                    const v = sheet['!data'] && sheet['!data'][row] && sheet['!data'][row][col] ? sheet['!data'][row][col].v : null;
                                    rowData.push(v ? v.toString() : '');
                                }
                                if (rowData.every((str) => !str?.trim())) {
                                    continue;
                                }
                                sheetData.push(rowData);
                            }

                            if (sheetData.length === 0) {
                                continue;
                            }

                            if (sheetData[0].length !== 2) {
                                throw new Error('Invalid sheet format');
                            }

                            if (sheetData[0][0] !== 'Key' || sheetData[0][1] !== 'Translation') {
                                throw new Error('Invalid sheet format');
                            }

                            for (let i = 1; i < sheetData.length; i++) {
                                const key = sheetData[i][0];
                                if (key in original) {
                                    const translation = sheetData[i][1];
                                    updated[key] = translation;
                                }
                            }

                            setPatch(updated);
                        }
                    } catch (error) {
                        logError('Error parsing translations', { Error: serializeError(error) });
                    }
                });
            });
        },
        accept: {
            'application/json': ['.json'],
            'application/vnd.ms-excel': ['.xls', '.xlsx', '.csv', '.ods', '.fods', '.txt']
        }
    });

    const exportTranslations = async () => {
        const xlsx = await import('xlsx');
        const workbook = xlsx.utils.book_new();
        workbook.Props = {
            Application: 'OnlineWebinar.com',
            AppVersion: '1.0.0',
            Author: 'OnlineWebinar.com',
            CreatedDate: new Date(),
            Title: 'Translations'
        };

        const sheetData: string[][] = [['Key', 'Translation'], ...Object.entries({ ...original, ...patch }).map(([key, value]) => [key, value])];
        const worksheet = xlsx.utils.aoa_to_sheet(sheetData, { cellDates: true });
        xlsx.utils.book_append_sheet(workbook, worksheet, 'Translations');
        xlsx.writeFileXLSX(workbook, 'Translations.xlsx');
    };

    const savePatchMutation = useMutation(OptionsApi.translation.update());
    const savePatch = () => {
        savePatchMutation.mutate(
            { language, patch },
            {
                onSuccess: () => {
                    resources[language].translation = { ...defaultResources[language].translation, ...patch };
                    loadPatch(language);
                }
            }
        );
    };

    return (
        <ContainerDefault>
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('Translation.PageHeading')}
            </Heading>
            <div className="max-w-300">
                <div className="max-w-4xl bg-info rounded-lg flex mb-4 p-4 md:p-5">
                    <Info classNames="w-8 h-8 flex-none -mt-1 mr-4 -ml-1" />
                    <p className="">{t('Translation.InfoText')}</p>
                </div>
                <div
                    {...getRootProps()}
                    className={`group relative w-full min-h-64 ${isDragActive && 'bg-accent-lighter'} hover:bg-accent-lighter focus-visible:bg-accent-lighter border-2 border-dashed ${!isDragActive && 'border-gray-500'} ${isDragActive && 'border-accent'} hover:border-accent focus-visible:border-accent rounded-lg flex mb-4 md:mb-6 outline-hidden overflow-hidden p-6 transition-colors duration-150`}
                >
                    <div className="flex flex-col items-center m-auto">
                        <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                        <p className="font-accent font-medium text-3xl mb-2">{isDragActive ? t('Generic.DragDrop.Active') : t('Generic.DragDrop')}</p>
                        <p>
                            <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                        </p>
                        <p className="mt-4">
                            {t('Generic.DragDrop.AcceptedText')}
                            <span className="font-medium">.json, {t('ImportInvites.DragDrop.AcceptedFileTypes')}</span>
                        </p>
                        {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
                    </div>
                    <input {...getInputProps()} />
                </div>

                <table>
                    <thead className="text-left">
                        <tr>
                            <th className="w-2/5">{t('Translation.Table.Header.Key')}</th>
                            <th>{t('Translation.Table.Header.Translation')}</th>
                        </tr>
                    </thead>
                    <tbody className="[&>tr>td]:first-of-type:break-all [&>tr>td]:pe-4">
                        {Object.keys(original).map((key) => (
                            <tr key={key}>
                                <td>{key}</td>
                                <td>
                                    <Input
                                        type="text"
                                        value={patch[key] ?? ''}
                                        onChange={(e) => {
                                            setPatch({ ...patch, [key]: e.target.value });
                                        }}
                                        label={original[key]}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="sticky bottom-0 bg-gray-100/50 backdrop-blur-xs flex justify-between gap-4 py-4">
                    <Button onClick={exportTranslations} variant="primary-outline">
                        {t('Generic.Button.Export')}
                    </Button>
                    <Button onClick={savePatch} variant="primary" isLoading={savePatchMutation.isPending}>
                        {t('Generic.Button.SaveChanges')}
                    </Button>
                </div>
            </div>
        </ContainerDefault>
    );
};
